import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { getAdminSolutions, deleteSolution, reorderSolutions, updateSolution } from '../../api/content';
import ContentCategoryManager from '../../components/admin/ContentCategoryManager';

const SolutionManagement = () => {
    const navigate = useNavigate();
    const [solutions, setSolutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isReorderMode, setIsReorderMode] = useState(false);
    const [reorderList, setReorderList] = useState([]);
    const [draggedIndex, setDraggedIndex] = useState(null);
    // 카테고리 관리는 리스트 상단과 편집 화면 두 곳에서 모두 연다 —
    // 사례 관리와 자리를 맞췄다. 한쪽에만 있으면 찾다가 못 찾는다.
    const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);

    useEffect(() => {
        loadSolutions();
    }, []);

    const loadSolutions = async () => {
        try {
            const data = await getAdminSolutions();
            setSolutions(data);
        } catch (error) {
            console.error('Failed to load solutions', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('정말 삭제하시겠습니까?')) {
            try {
                await deleteSolution(id);
                alert('삭제되었습니다.');
                loadSolutions();
            } catch (error) {
                console.error('Failed to delete solution', error);
                alert('삭제 실패');
            }
        }
    };

    const handleToggleVisibility = async (solution) => {
        try {
            await updateSolution(solution.id, { isPublic: !solution.isPublic });
            loadSolutions();
        } catch (error) {
            console.error('Failed to toggle visibility', error);
            alert('상태 변경 실패');
        }
    };

    const openReorderMode = () => {
        setReorderList([...solutions]);
        setIsReorderMode(true);
    };

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newList = [...reorderList];
        const [draggedItem] = newList.splice(draggedIndex, 1);
        newList.splice(index, 0, draggedItem);
        setReorderList(newList);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleSaveReorder = async () => {
        try {
            const ids = reorderList.map(item => item.id);
            await reorderSolutions(ids);
            alert('순서가 저장되었습니다.');
            setIsReorderMode(false);
            loadSolutions();
        } catch (error) {
            console.error(error);
            alert('순서 저장 실패');
        }
    };

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <h2 className="text-2xl font-extrabold text-gray-800 m-0 tracking-tight">솔루션 관리</h2>
                <div className="flex flex-wrap gap-2 w-full md:w-auto">
                    <button
                        onClick={() => setCategoryManagerOpen(true)}
                        className="flex-1 md:flex-none px-5 py-2.5 bg-white hover:bg-gray-50 text-gray-700 font-bold border border-gray-200 rounded-xl shadow-sm transition-colors text-sm"
                    >
                        ⚙️ 카테고리 관리
                    </button>
                    <button
                        onClick={openReorderMode}
                        className="flex-1 md:flex-none px-5 py-2.5 bg-white hover:bg-gray-50 text-gray-700 font-bold border border-gray-200 rounded-xl shadow-sm transition-colors text-sm"
                    >
                        ↕ 순서 변경
                    </button>
                    <button
                        onClick={() => navigate('/admin/solutions/new')}
                        className="flex-1 md:flex-none px-5 py-2.5 bg-gray-800 hover:bg-gray-900 text-white font-bold rounded-xl shadow-sm transition-colors text-sm"
                    >
                        + 솔루션 등록
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                {loading ? (
                    <div className="p-10 text-center text-gray-400 font-bold animate-pulse">데이터 로딩 중...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="px-5 py-4 font-bold w-20">썸네일</th>
                                    <th className="px-5 py-4 font-bold">제목</th>
                                    <th className="px-5 py-4 font-bold w-32">카테고리</th>
                                    <th className="px-5 py-4 font-bold w-24 text-center">공개여부</th>
                                    <th className="px-5 py-4 font-bold w-20 text-center">조회수</th>
                                    <th className="px-5 py-4 font-bold w-32 text-center">관리</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-gray-700">
                                {solutions.map(solution => (
                                    <tr key={solution.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-5 py-3">
                                            {solution.thumbnail ? (
                                                <img
                                                    src={solution.thumbnail}
                                                    alt={solution.title}
                                                    className="w-12 h-12 object-cover rounded-md shadow-sm border border-gray-100"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center text-xs text-gray-400">N/A</div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3">
                                            <div className="font-extrabold text-gray-900 text-base">{solution.title}</div>
                                            {solution.subtitle && <div className="text-sm font-medium text-gray-500 mt-0.5">{solution.subtitle}</div>}
                                        </td>
                                        <td className="px-5 py-3 font-semibold"><span className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-md text-xs">{solution.categoryLabel || '-'}{solution.categories?.length > 1 ? ` +${solution.categories.length - 1}` : ''}</span></td>
                                        <td className="px-5 py-3 text-center">
                                            <button
                                                onClick={() => handleToggleVisibility(solution)}
                                                className={`mx-auto flex justify-center items-center gap-1.5 px-3 py-1.5 w-20 rounded-lg text-xs font-bold transition-all shadow-sm tracking-wide ${solution.isPublic ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200'}`}
                                                title={solution.isPublic ? '클릭하면 비공개로 변경' : '클릭하면 공개로 변경'}
                                            >
                                                {solution.isPublic ? '✅ 공개' : '🔒 비공개'}
                                            </button>
                                        </td>
                                        <td className="px-5 py-3 text-center font-mono text-gray-500">{solution.viewCount}</td>
                                        <td className="px-5 py-3 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => navigate(`/admin/solutions/${solution.id}`)}
                                                    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition-colors"
                                                >
                                                    수정
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(solution.id)}
                                                    className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold rounded-lg transition-colors"
                                                >
                                                    삭제
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {solutions.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="p-10 text-center text-gray-400 font-bold bg-gray-50">
                                            등록된 솔루션이 없습니다.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Reorder Modal */}
            {isReorderMode && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden p-6">
                        <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-100">
                            <h3 className="text-xl font-bold text-gray-800 m-0">솔루션 순서 변경</h3>
                            <button onClick={() => setIsReorderMode(false)} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none bg-transparent outline-none">&times;</button>
                        </div>
                        <p className="text-sm text-gray-500 mb-4 bg-gray-50 p-3 rounded-lg border border-gray-100 font-medium">
                            드래그하여 순서를 변경하세요.
                        </p>
                        <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                            {reorderList.map((item, index) => (
                                <div
                                    key={item.id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDragEnd={handleDragEnd}
                                    className={`flex items-center gap-4 p-3 rounded-xl border cursor-grab transition-colors active:cursor-grabbing ${draggedIndex === index ? 'bg-primary-50 border-primary-200 shadow-md scale-[1.02]' : 'bg-white border-gray-200 shadow-sm hover:border-gray-300'}`}
                                >
                                    <span className="text-gray-400 text-lg cursor-grab">☰</span>
                                    <span className="w-8 h-8 rounded-full bg-gray-800 text-white flex items-center justify-center text-xs font-bold shadow-sm shrink-0">
                                        {index + 1}
                                    </span>
                                    <div className="flex-1 font-bold text-gray-800 truncate">
                                        {item.title}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2 mt-6 pt-4 border-t border-gray-100 shrink-0">
                            <button
                                onClick={() => setIsReorderMode(false)}
                                className="flex-1 py-2.5 bg-white text-gray-700 font-bold border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleSaveReorder}
                                className="flex-1 py-2.5 bg-gray-800 text-white font-bold rounded-xl hover:bg-gray-900 transition-colors shadow-sm"
                            >
                                순서 저장 결재
                            </button>
                        </div>
                    </div>
                </div>
            )}
        
            {/* 카테고리를 고치면 목록의 표시 이름도 바뀌므로 닫을 때 다시 읽는다. */}
            <ContentCategoryManager
                isOpen={categoryManagerOpen}
                onClose={() => { setCategoryManagerOpen(false); loadSolutions(); }}
                contentType="solution"
                title="솔루션 카테고리 관리"
            />

        </AdminLayout>
    );
};

export default SolutionManagement;
